// src/routes/merchants/picker/MerchantPickerDialog.tsx
import { useCallback, useState, type RefObject } from 'react'
import { useEntitlement } from '../../../platform/access/useEntitlement'
import { Async, type AsyncState } from '../../../platform/async/Async'
import { Skeleton } from '../../../platform/async/Skeleton'
import { Overlay } from '../../../platform/overlay/Overlay'
import { Button } from '../../../platform/primitives/Button'
import { Money } from '../../../platform/primitives/Money'
import { useBreakpoint } from '../../../platform/responsive/useBreakpoint'
import { shortDate } from '../../transactions/txnDisplay'
import { BrandConfirm } from '../catalogue/BrandConfirm'
import type { PickExtras } from '../catalogue/catalogueModel'
import { useBrandSearch } from '../catalogue/useCatalogue'
import { MerchantsError, useCreateMerchant } from '../useMerchants'
import { MerchantBrowser, type CreateMerchant } from './MerchantBrowser'
import { toPickerMerchant, type PickerMerchant } from './merchantPickerModel'
import { useDebouncedValue, useMerchantDirectory, useMostUsedMerchants, type DirectoryPage } from './useMerchantPicker'

/** The transaction the pick is for: `CategoryPickerDialog`'s context, plus the bank's own description. */
export type MerchantPickerContext = { merchant: string; date: string; amountCents: number; currency: string; raw: string }

/** Long enough that typing a word sends one request, short enough that the answer feels like the keystroke's. */
export const SEARCH_DEBOUNCE_MS = 250

/**
 * The merchant picker as a dialog — a sheet on a phone, wide on larger screens, `CategoryPickerDialog`'s
 * shape. The context header names the transaction and, under it in monospace, the bank's raw
 * description: that is the text a user matches a merchant against.
 *
 * `onPick` receives the id and, for a real pick, the merchant itself, so a field can name a choice the
 * transaction does not carry yet. "Remove merchant" sends `null`, and is offered only for a row that
 * has a merchant.
 *
 * A brand added from the picker's Brands section is a pick like any other; when the user also kept "Also
 * set category", `extras.categoryId` comes with it, and the caller sets both. That option is offered only
 * when the caller says the transaction has no category (`offerCategory`).
 *
 * Callers already inside an Overlay (bulk) embed `MerchantBrowserLoader` instead.
 */
export function MerchantPickerDialog({
  open,
  onClose,
  value,
  onPick,
  context,
  allowNone = false,
  offerCategory = false,
  restoreFocusTo,
}: {
  open: boolean
  onClose: () => void
  value: string | null
  onPick: (id: string | null, merchant?: PickerMerchant, extras?: PickExtras) => void
  context?: MerchantPickerContext
  allowNone?: boolean
  /** The transaction has no category yet: a brand's confirm step may offer to set its suggested one. */
  offerCategory?: boolean
  restoreFocusTo?: RefObject<HTMLElement | null>
}) {
  const sheet = useBreakpoint() === 'phone'
  if (!open) return null
  const pick = (m: PickerMerchant | null, extras?: PickExtras) => {
    if (m === null) onPick(null)
    else if (extras === undefined) onPick(m.id, m)
    else onPick(m.id, m, extras)
    onClose()
  }
  return (
    <Overlay open onClose={onClose} label="Choose a merchant" placement="sheet" size="wide" restoreFocusTo={restoreFocusTo}>
      <div className={sheet ? 'dialog merchant-dialog merchant-dialog--sheet' : 'dialog merchant-dialog'}>
        <h2 className="dialog__title">Choose a merchant</h2>
        {context !== undefined && (
          <div className="merchant-dialog__context">
            <p className="merchant-dialog__line">
              <span>{`${context.merchant} · ${shortDate(context.date)}`}</span>
              <Money cents={context.amountCents} currency={context.currency} kind="flow" />
            </p>
            <p className="merchant-dialog__line">
              <span className="sr-only">Bank description: </span>
              <span className="merchant-dialog__raw">{context.raw}</span>
            </p>
          </div>
        )}
        <MerchantBrowserLoader value={value} onPick={pick} offerCategory={offerCategory} autoFocus className="merchant-dialog__browser" />
        <div className="dialog__actions">
          {allowNone && value !== null && (
            <Button type="button" size="page" variant="outline" onClick={() => pick(null)}>
              Remove merchant
            </Button>
          )}
          <Button type="button" size="page" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Overlay>
  )
}

/**
 * The browser with its own data — for a caller already inside a dialog (bulk), and the dialog's body.
 *
 * Owns every request the browser needs: the A–Z pages, the debounced server search, "Most used", and
 * the create mutation (offered only under `custom_merchants`, read the way `Merchants.tsx` reads it), and
 * the brand search under the same entitlement.
 *
 * **Brands need `custom_merchants`.** Adding a brand creates the user's own merchant, which is what the
 * entitlement gates. The picker has no upgrade affordance of its own (without it, Create is simply
 * absent), so without it the Brands section is hidden and the catalogue is never asked.
 * It shows the browser as soon as the first page answers. "Most used" never holds it: that section has
 * its own placeholder while it loads, and on a failure it is simply absent.
 */
export function MerchantBrowserLoader({
  value,
  onPick,
  offerCategory = false,
  autoFocus,
  className,
}: {
  value: string | null
  onPick: (merchant: PickerMerchant, extras?: PickExtras) => void
  /** See `MerchantPickerDialog`: the transaction has no category yet. Bulk and rules have no one transaction, so never. */
  offerCategory?: boolean
  autoFocus?: boolean
  /** For Async's busy wrapper, which is the box a flex parent lays out. */
  className?: string
}) {
  const [query, setQuery] = useState('')
  const term = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS)
  const browse = useMerchantDirectory('')
  const search = useMerchantDirectory(term, { enabled: term !== '' })
  const mostUsed = useMostUsedMerchants()
  const entitled = useEntitlement('custom_merchants')
  const brands = useBrandSearch(term, { enabled: entitled })
  const { mutateAsync } = useCreateMerchant()
  const createMerchant = useCallback<CreateMerchant>(
    async (name) => {
      try {
        return { ok: true, merchant: toPickerMerchant(await mutateAsync({ name, type: 'business' })) }
      } catch (err) {
        return { ok: false, status: err instanceof MerchantsError ? err.status : null }
      }
    },
    [mutateAsync],
  )

  const state: AsyncState<DirectoryPage> = browse.state.status === 'idle' ? { status: 'loading' } : browse.state

  return (
    <Async
      surface="merchants"
      state={state}
      className={className}
      skeleton={<Skeleton height={320} />}
      // Required by Async's type; unreachable — no `isEmpty` is passed, because a user with no merchants still searches and creates.
      empty={{ title: 'No merchants yet' }}
    >
      {(page) => (
        <MerchantBrowser
          value={value}
          onPick={onPick}
          query={query}
          onQueryChange={setQuery}
          mostUsed={mostUsed.merchants}
          mostUsedLoading={!mostUsed.settled}
          directory={{ merchants: page.merchants, hasMore: browse.hasMore, loadingMore: browse.loadingMore, loadMore: browse.loadMore }}
          search={{ state: search.state, term, hasMore: search.hasMore, loadingMore: search.loadingMore, loadMore: search.loadMore }}
          create={entitled ? createMerchant : null}
          brands={
            entitled
              ? {
                  ...brands,
                  term,
                  confirm: (brand, cancel) => (
                    <BrandConfirm key={brand.id} brand={brand} offerCategory={offerCategory} onAdded={onPick} onCancel={cancel} />
                  ),
                }
              : null
          }
          autoFocus={autoFocus}
        />
      )}
    </Async>
  )
}
